import { isDefined } from '@sgme/fp';
import { BlockInvalidFieldsCount } from 'components/common-form/builder/form/node/block/components/BlockInvalidFieldsCount';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import { InvalidFieldsContext } from 'components/common-form/contexts/InvalidFieldsContext';
import { AddonActionComponent as AddonAction } from 'components/PostTrade/Addon/components/AddonAction';
import { SalesBeneficiary } from 'components/PostTrade/Addon/components/SalesBeneficiary';
import { DateField } from 'components/PostTrade/TradeAllocation/components/DateField';
import { FormattedMessage } from 'components/shared/Intl';
import type { QuoteKeys, QuoteKeysPrefix } from 'locales/quoteKeys';
import type { FormRecord } from 'models/formError';
import { FC, ReactNode, useMemo } from 'react';
import { Card, CardBody, CardTitle } from 'react-bootstrap';
import type { InvalidFields } from 'state/selectors/meta/static/utils';
import { toInvalidFields } from 'state/selectors/meta/static/utils';
import { nowISODateString } from 'utils/date';

import type { AddonMainFormProps } from './AddonMainForm.connect';
import { connectAddonMainForm } from './AddonMainForm.connect';
import type { IAddonMainFormContext } from './AddonMainForm.context';
import { AddonMainFormContext } from './AddonMainForm.context';
import { AddonNotional } from './components/AddonNotional';
import { BackToBackBlock } from './components/BackToBackBlock';
import { DailyNumberOfShares } from './components/DailyNumberOfShares';
import { DealSplit } from './components/DealSplit';
import { FileCode } from './components/FileCode';
import { IsinCode } from './components/IsinCode';
import { MergeDeals } from './components/MergeDeals';
import { NotionalTypeComponent as NotionalType } from './components/NotionalType';
import { PercentField } from './components/PercentField';
import { Product } from './components/Product';
import { RemunerationMode } from './components/RemunerationMode';
import { SalesCounterpart } from './components/SalesCounterpart';
import { SalesDesk } from './components/SalesDesk';
import { SolveFor } from './components/SolveFor';
import { TotalNumberOfAccumulationDays } from './components/TotalNumberOfAccumulationDays';
import { Wrapper } from './components/Wrapper';
import { ValidationErrorsContext } from './ValidationErrorsContext';

const emptyValidationErrors: readonly [] = [];

const TwoCol: FC<{ left: ReactNode; right?: ReactNode }> = ({ left, right }) => (
  <div className="row">
    <div className="col-6">{left}</div>
    {right && <div className="col-6">{right}</div>}
  </div>
);

const Component: FC<AddonMainFormProps> = ({
  instrumentContractName,
  wrapper,
  isinCode,
  fileCode,
  isOtc,
  isShareAccDec,
  custodians,
  dealSplit,
  tradeClientSide,
  addonAction,
  selectedOnBehalfClient,
  addonNotional,
  addonNotionalInUnits,
  notionalType,
  dailyNumberOfShares,
  currency,
  fundingLockDate,
  mergeableDeals,
  mergeDealsRequested,
  selectedTradeReference,
  remunerationMode,
  solveModes,
  solveFor,
  totalNumberOfAccumulationDays,
  xMargin,
  upFrontFee,
  offerPrice,
  fixedAmount,
  validationErrors,
  isReadOnly,
  isLoading,
  salesBeneficiary,
  salesDesk,
  availableSalesDesks,
  availableSalesBeneficiaries,
  isBackToBack,
  backToBackFileCode,
  backToBackIsDeltaAdjusted,
  backToBackNoteNominal,
  backToBackType,
  initialBackToBackType,
  isDelayedBackToBack,
  onAddonNotionalChange,
  onDailyNumberOfShareChanged,
  onNotionalTypeChange,
  onSelectedOnBehalfClientChange,
  onAddonActionChange,
  onDealSplitChange,
  onMergeDealsRequestedChange,
  onSelectedTradeReferenceChange,
  onRemunerationModeChange,
  onSolveModeChange,
  onXMarginChange,
  onUpFrontFeeChange,
  onOfferPriceChange,
  onFixedAmountChange,
  onFundingLockDateChange,
  onBackToBackFileCodeChange,
  onBackToBackIsBackToBackChange,
  onBackToBackIsDelayedBackToBackChange,
  onBackToBackIsDeltaAdjustedChange,
  onBackToBackNoteNominalChange,
  onBackToBackTypeChange,
  onSalesBeneficiaryChange,
  onSalesDeskChange,
}) => {
  const now = nowISODateString();
  const formContext = useMemo<IAddonMainFormContext>(
    () => ({
      isReadOnly,
      isLoading,
    }),
    [isReadOnly, isLoading],
  );

  const invalidFields = useMemo<InvalidFields<FormRecord, QuoteKeys, QuoteKeysPrefix>>(
    () => toInvalidFields<FormRecord, QuoteKeys, QuoteKeysPrefix>(validationErrors),
    [validationErrors],
  );

  return (
    <FormOrientationContext.Provider value="vertical">
      <InvalidFieldsContext.Provider value={invalidFields as InvalidFields}>
        <ValidationErrorsContext.Provider value={emptyValidationErrors}>
          <AddonMainFormContext.Provider value={formContext}>
            <div
              className="main-form Default standard-side-panel container-fluid overflow-y-auto d-flex flex-column"
              data-e2e="addon-form"
            >
              <div className="row py-1" data-e2e="addon-upper-container">
                <div className="col-sm-12 col-md-6 px-1">
                  <Card>
                    <CardBody className="py-3">
                      <CardTitle as="h6">
                        <div className="d-flex align-items-end">
                          <FormattedMessage id="addon.quoteRequest.title" />
                          <BlockInvalidFieldsCount
                            blockId="addOn_quoteRequest"
                            fieldIds={[
                              'addonNotional',
                              'dailyNumberOfShares',
                              'totalNumberOfAccumulationDays',
                              'salesCounterpart',
                              'dealSplit',
                            ]}
                          />
                        </div>
                      </CardTitle>
                      <TwoCol left={<IsinCode isinCode={isinCode} />} right={<FileCode fileCode={fileCode} />} />
                      <TwoCol
                        left={<Product product={instrumentContractName} />}
                        right={<Wrapper wrapper={wrapper} />}
                      />
                      <AddonAction addonAction={addonAction} onChange={onAddonActionChange} />
                      <NotionalType
                        visible={isShareAccDec}
                        notionalType={notionalType}
                        notionalTypes={['Currency', 'Units']}
                        onChange={onNotionalTypeChange}
                      />
                      <AddonNotional
                        notional={addonNotional}
                        currency={currency}
                        onChange={onAddonNotionalChange}
                        visible={notionalType === undefined || notionalType === 'Currency'}
                      />
                      <DailyNumberOfShares
                        dailyNumberOfShares={dailyNumberOfShares}
                        onChange={onDailyNumberOfShareChanged}
                        visible={notionalType === 'Units'}
                      />
                      <TotalNumberOfAccumulationDays
                        totalNumberOfAccumulationDays={totalNumberOfAccumulationDays}
                        visible={notionalType === 'Units'}
                      />
                      <SalesCounterpart
                        selectedOnBehalfClient={selectedOnBehalfClient ?? null}
                        onChange={onSelectedOnBehalfClientChange}
                        isDisabled={isOtc || isReadOnly}
                      />
                      <DealSplit
                        availableCustodians={custodians}
                        currency={notionalType === 'Currency' ? currency : 'Units'}
                        dealSplit={dealSplit}
                        notionalToSplit={notionalType === 'Currency' ? addonNotional : addonNotionalInUnits}
                        onChange={onDealSplitChange}
                        isReadonly={isOtc || isReadOnly}
                      />
                    </CardBody>
                  </Card>
                </div>
                <div className="col-sm-12 col-md-6 px-1">
                  <Card>
                    <CardBody className="py-3">
                      <CardTitle as="h6">
                        <div className="d-flex align-items-end">
                          <FormattedMessage id="addon.solve.title" />
                          <BlockInvalidFieldsCount
                            blockId="addOn_solve"
                            fieldIds={['xMargin', 'upFrontFee', 'offerPrice']}
                          />
                        </div>
                      </CardTitle>
                      <RemunerationMode
                        value={remunerationMode}
                        onChange={onRemunerationModeChange}
                        visible
                        wrapper={wrapper}
                      />
                      <SolveFor value={solveFor} onChange={onSolveModeChange} solveModes={solveModes ?? []} />
                      <PercentField
                        field="xMargin"
                        value={xMargin}
                        onChange={onXMarginChange}
                        visible={solveFor !== 'XMargin'}
                      />
                      <PercentField
                        field="upFrontFee"
                        label="upfrontFee"
                        value={upFrontFee}
                        onChange={onUpFrontFeeChange}
                        visible={remunerationMode === 'Upfront' && solveFor !== 'UpfrontFee'}
                      />
                      <PercentField
                        field="offerPrice"
                        value={offerPrice}
                        onChange={onOfferPriceChange}
                        visible={remunerationMode === 'Reoffer' && solveFor !== 'OfferPrice'}
                      />
                      <PercentField
                        field="fixedAmount"
                        value={fixedAmount}
                        onChange={onFixedAmountChange}
                        visible={remunerationMode === 'Premium' && solveFor !== 'FixedAmount'}
                      />
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody className="py-3">
                      <CardTitle as="h6">
                        <div className="d-flex align-items-end">
                          <FormattedMessage id="addon.additionalFeatures.title" />
                        </div>
                      </CardTitle>
                      <DateField
                        id="fundingLockDate"
                        value={fundingLockDate}
                        onChange={onFundingLockDateChange}
                        isClearable={true}
                        isDisabled={isReadOnly}
                        isLoading={isLoading}
                        displayPresets={false}
                        maxDate={now}
                      />
                    </CardBody>
                  </Card>
                  {(isLoading || (isDefined(isBackToBack) && isDefined(backToBackType))) && (
                    <BackToBackBlock
                      currency={currency}
                      fileCode={backToBackFileCode}
                      isBackToBack={isBackToBack ?? false}
                      isBackToBackReadonly={initialBackToBackType === 'HedgingTrade'}
                      isBackToBackTypeReadonly={initialBackToBackType === 'HedgingTrade'}
                      isDelayedBackToBack={isDelayedBackToBack}
                      isDeltaAdjusted={backToBackIsDeltaAdjusted}
                      noteNominal={backToBackNoteNominal}
                      onFileCodeChange={onBackToBackFileCodeChange}
                      onIsBackToBackChange={onBackToBackIsBackToBackChange}
                      onIsDelayedBackToBackChange={onBackToBackIsDelayedBackToBackChange}
                      onIsDeltaAdjustedChange={onBackToBackIsDeltaAdjustedChange}
                      onNoteNominalChange={onBackToBackNoteNominalChange}
                      onTypeChange={onBackToBackTypeChange}
                      type={backToBackType ?? 'None'}
                    />
                  )}
                  <Card>
                    <CardBody className="py-3">
                      <CardTitle as="h6">
                        <div className="d-flex align-items-end">
                          <FormattedMessage id="addon.actors.title" />
                        </div>
                      </CardTitle>
                      <SalesBeneficiary
                        value={salesBeneficiary}
                        availableItems={availableSalesBeneficiaries}
                        onChange={onSalesBeneficiaryChange}
                      />
                      <SalesDesk value={salesDesk} availableItems={availableSalesDesks} onChange={onSalesDeskChange} />
                    </CardBody>
                  </Card>
                </div>
              </div>
              <div className="row py-1 flex-grow-1" data-e2e="addon-lower-container">
                <div className="col-12 px-1">
                  <Card className="h-100">
                    <CardBody className="py-3">
                      <CardTitle as="h6">
                        <div className="d-flex align-items-end">
                          <FormattedMessage id="addon.mergeDeals.title" />
                          <BlockInvalidFieldsCount blockId="addOn_mergedDeals" fieldIds={['mergeDeals']} />
                        </div>
                      </CardTitle>
                      <MergeDeals
                        deals={mergeableDeals}
                        selectedTradeId={selectedTradeReference}
                        mergeDealsRequested={mergeDealsRequested ?? false}
                        isSwitchDisabled={isOtc || tradeClientSide === 'sell' || addonAction === 'Decrease'}
                        onLineSelected={onSelectedTradeReferenceChange}
                        onMergeDealsRequestedChange={onMergeDealsRequestedChange}
                      />
                    </CardBody>
                  </Card>
                </div>
              </div>
            </div>
          </AddonMainFormContext.Provider>
        </ValidationErrorsContext.Provider>
      </InvalidFieldsContext.Provider>
    </FormOrientationContext.Provider>
  );
};

Component.displayName = 'AddonFormLoaded';

export const AddonMainForm = connectAddonMainForm(Component);
